/**
 * useImmersiveTheme
 * Light (default) / dark theme for the immersive catalog overlay, shared as a
 * module-level singleton so the overlay (which applies the `.theme-light` class)
 * and the corner toggle button stay in sync without prop plumbing. The choice is
 * persisted to localStorage and read once on module load, so reopening the
 * overlay (or reloading the app) restores the last chosen theme.
 *
 * `isSwitching` is true for a short window after each toggle; the overlay applies
 * a `.theme-switching` class during that window to enable a brief color
 * cross-fade (disabled under prefers-reduced-motion via CSS).
 */
import { ref } from 'vue'

const STORAGE_KEY = 'gofood:immersiveTheme'
// Slightly longer than the CSS cross-fade so transitions finish cleanly.
const SWITCH_WINDOW_MS = 380

function loadTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    // localStorage unavailable (private mode / blocked) -> default light
    return 'light'
  }
}

const theme = ref(loadTheme()) // 'dark' | 'light'
const isSwitching = ref(false)
let switchTimeout = null

function toggleTheme() {
  theme.value = theme.value === 'light' ? 'dark' : 'light'
  try {
    localStorage.setItem(STORAGE_KEY, theme.value)
  } catch {
    // Persistence is best-effort; the in-session choice still applies.
  }
  if (switchTimeout) clearTimeout(switchTimeout)
  isSwitching.value = true
  switchTimeout = setTimeout(() => {
    isSwitching.value = false
  }, SWITCH_WINDOW_MS)
}

export function useImmersiveTheme() {
  return { theme, isSwitching, toggleTheme }
}
